import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import FormattedCurrencyContainer from '../../../../components/formatted-currency/container';
import commonMessages from '../common-messages';
import { emptyArray } from 'ui-components/default-props/empty';

import style from '../common-styles.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const TITLE = 'title';
const NAME = 'name';
const QTY = 'qty';
const REVENUE = 'revenue';

const dataSourcesToAlign = [QTY, REVENUE];

const messages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Most Valuable Customers',
    id: 'AKyo+u',
    description: 'most valuable customers table header',
  },
  [NAME]: {
    defaultMessage: 'Name',
    id: 'fKfeGR',
    description: 'most valuable customers table name header',
  },
  [QTY]: {
    defaultMessage: 'Qty',
    id: '9ymOUA',
    description: 'most valuable customers table quantity (abbreviated) header',
  },
  [REVENUE]: {
    defaultMessage: 'Revenue',
    id: 'Eu5ou2',
    description: 'most valuable customers table share header',
  },
});

type CustomerRow = {
  name: string;
  qty: string;
  revenue: React.ReactNode;
};

type CustomerData = {
  name: string;
  count: number;
  amount: number;
  customerId: number;
};

type MostValuableCustomerTableProps = {
  data?: CustomerData[];
};

export const MostValuableCustomerTable = ({ data = emptyArray as CustomerData[] }: MostValuableCustomerTableProps) => {
  const intl = useIntl();
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const tableTitle = useMemo(() => intl.formatMessage(messages[TITLE]), [intl]);

  const emptyRow = useMemo(() => (!data.length ? <FormattedMessage {...commonMessages.nullStateMessage} /> : null), [data.length]);

  const renderAlignedHeader = useCallback(
    (accessor: keyof typeof messages) => {
      const title = intl.formatMessage(messages[accessor]);
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {title}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{title}</div>
      ) : (
        title
      );
    },
    [intl, isShipStationAnalyticsFeatureEnabled],
  );

  const renderAlignedCell = useCallback(
    (value: React.ReactNode, accessor: string) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {value}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{value}</div>
      ) : (
        value
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const columns = useMemo(
    () => [
      {
        Header: () => renderAlignedHeader(NAME),
        accessor: NAME as typeof NAME,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, NAME),
      },
      {
        Header: () => renderAlignedHeader(QTY),
        accessor: QTY as typeof QTY,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, QTY),
      },
      {
        Header: () => renderAlignedHeader(REVENUE),
        accessor: REVENUE as typeof REVENUE,
        Cell: ({ value }: { value: React.ReactNode }) => renderAlignedCell(value, REVENUE),
      },
    ],
    [renderAlignedHeader, renderAlignedCell],
  );

  const tableData = useMemo(() => {
    return data.map((customer) => ({
      name: customer.name,
      qty: intl.formatNumber(customer.count),
      revenue: <FormattedCurrencyContainer value={customer.amount} />,
    }));
  }, [data, intl]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<CustomerRow>(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div
        className={classnames(
          { [style['table-title']]: !isShipStationAnalyticsFeatureEnabled },
          { [analyticsStyle['analytics-table-title']]: isShipStationAnalyticsFeatureEnabled },
        )}
      >
        <Text textType={TextType.H3} value={tableTitle} />
      </div>
      <ReactTableWrapper
        className={classnames({
          [analyticsStyle['analytics-table']]: isShipStationAnalyticsFeatureEnabled,
        })}
        emptyRow={emptyRow}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};
